/**
 * @file session.js
 * @description Session 管理服务，负责 session_key 的获取、刷新和错误处理
 */

import axios from 'axios'
import { Toast } from 'vant'
import util from '@/libs/util'
import clientConfig from '@/config/clientConfig'

// 默认值，当配置不可用时使用
const DEFAULT_CH_APP_ID = 'unPUbmZ_zJYe2B-7Hkf4XK6fK3x9TmkacLdkOL2Mlj0='
const DEFAULT_CORP_ID = 'ww36ba8f38931cf88c'

// 控制并发刷新标志
let isRefreshing = false 
// 请求队列，存储等待 Session 刷新的请求
const requestQueue = []
// 最大重试次数
const MAX_RETRY_COUNT = 3
// 当前重试次数
let retryCount = 0

// 定时器引用
let sessionCheckTimer = null

/**
 * 获取 API 基础 URL
 */
function getBaseURL() {
  if (process.env.NODE_ENV === 'development') {
    return `${location.origin}/api/tccc`
  } else {
    return `${location.origin}/tccc`
  }
}

/**
 * 获取用户ID
 */
function getUserId() {
  // 从 localStorage 获取
  let user_id = util.localStorage.get('user_id')
  if (user_id) return user_id
  
  // 从 cookie 获取
  user_id = util.cookies.get('userid')
  if (user_id) return user_id
  
  return null
}

/**
 * 获取 CH_APP_ID
 * 从配置文件中获取，如果获取失败则使用默认值
 */
function getCHAppId() {
  try {
    const authConfig = clientConfig.getAuthConfig()
    return authConfig.appid || DEFAULT_CH_APP_ID
  } catch (error) {
    console.warn('获取 CH_APP_ID 配置失败，使用默认值:', error)
    return DEFAULT_CH_APP_ID
  }
}

/**
 * 获取 CORP_ID
 * 从配置文件中获取，如果获取失败则使用默认值
 */
function getCorpId() {
  try {
    const authConfig = clientConfig.getAuthConfig()
    return authConfig.corp_id || DEFAULT_CORP_ID
  } catch (error) {
    console.warn('获取 CORP_ID 配置失败，使用默认值:', error)
    return DEFAULT_CORP_ID
  }
}

/**
 * 刷新 Session Key
 * @description 获取新的 session_key 并存储到 localStorage 中
 * @returns {Promise<Object>} 包含 session_key 的对象
 */
export async function refreshSessionKey() {
  const startTime = performance.now();
  console.log('⏱️ [Session] refreshSessionKey 被调用');
  
  // 如果已经在刷新，则直接返回一个 Promise
  if (isRefreshing) {
    console.log('⏱️ [Session] Session 已在刷新中，等待完成');
    return new Promise((resolve) => {
      requestQueue.push(resolve);
    });
  }
  
  isRefreshing = true;
  
  try {
    // 1. 获取用户ID
    const userIdStartTime = performance.now();
    const user_id = getUserId();
    if (!user_id) {
      throw new Error('无法获取用户ID');
    }
    
    console.log(`⏱️ [Session] 获取用户ID成功: ${user_id}, 耗时: ${(performance.now() - userIdStartTime).toFixed(2)}ms`);
    
    // 获取配置的 CH_APP_ID 和 CORP_ID
    const configStartTime = performance.now();
    const ch_app_id = getCHAppId();
    const corp_id = getCorpId();
    console.log(`⏱️ [Session] 获取配置参数完成, 耗时: ${(performance.now() - configStartTime).toFixed(2)}ms`);
    
    // 同时执行步骤1和步骤2 - 并行请求提高效率
    console.log('⏱️ [Session] 并行执行: 获取登录code和access_token');
    
    // 创建两个Promise，同时发起
    const parallelStartTime = performance.now();
    const codePromise = axios({
      url: `${getBaseURL()}/secu_ctrl/user/login/code/`,
      method: 'get',
      params: {
        user_id,
        corp_id,
        ch_app_id
      }
    });
    
    const tokenPromise = axios({
      url: `${getBaseURL()}/secu_ctrl/client_side/get_access_token/`,
      method: 'get',
      params: {
        ch_app_id
      }
    });
    
    // 并行等待两个请求完成
    const [codeResponse, tokenResponse] = await Promise.all([codePromise, tokenPromise]);
    console.log(`⏱️ [Session] 并行请求完成, 耗时: ${(performance.now() - parallelStartTime).toFixed(2)}ms`);
    
    // 处理登录code响应
    const codeData = codeResponse.data;
    if (codeData.status_code !== 0 || !codeData.data?.code) {
      throw new Error(codeData.message || '获取登录 code 失败');
    }
    const code = codeData.data.code;
    console.log(`⏱️ [Session] 获取 code 成功: ${code}`);
    
    // 处理access_token响应
    const tokenData = tokenResponse.data;
    if (tokenData.status_code !== 0 || !tokenData.data?.token) {
      throw new Error(tokenData.message || '获取 access_token 失败');
    }
    const access_token = tokenData.data.token;
    console.log(`⏱️ [Session] 获取 access_token 成功: ${access_token.substr(0, 10)}...`);
    
    // 3. 获取 session_key - 需要前两步的结果
    console.log('⏱️ [Session] 步骤3: 获取 session_key');
    const sessionStartTime = performance.now();
    const sessionResponse = await axios({
      url: `${getBaseURL()}/secu_ctrl/user/session_key/`,
      method: 'get',
      params: {
        access_token,
        code,
        appid: ch_app_id,
        expires_secs:'7200'
      }
    });
    console.log(`⏱️ [Session] session_key请求完成, 耗时: ${(performance.now() - sessionStartTime).toFixed(2)}ms`);
    
    const sessionData = sessionResponse.data;
    if (sessionData.status_code !== 0 || !sessionData.data?.session_key) {
      throw new Error(sessionData.message || '获取 session_key 失败');
    }
    
    const session_key = sessionData.data.session_key;
    console.log(`⏱️ [Session] 获取 session_key 成功: ${session_key.substr(0, 10)}...`);
    
    // 5. 保存 session_key
    const saveStartTime = performance.now();
    const expiresIn = 5 * 60; // 5分钟有效期
    const expiresTime = new Date(new Date().getTime() + expiresIn * 1000);
    
    util.localStorage.set('session_key', session_key);
    util.localStorage.set('session_key_expires_time', expiresTime.toISOString());
    util.localStorage.set('user_id', user_id);
    
    // 存储会话信息对象
    const sessionInfo = {
      session_key,
      user_id,
      expires_time: expiresTime.toISOString()
    };
    util.localStorage.set('session_info', JSON.stringify(sessionInfo));
    console.log(`⏱️ [Session] 保存session信息完成, 耗时: ${(performance.now() - saveStartTime).toFixed(2)}ms`);
    
    // 重置重试计数
    retryCount = 0;
    
    // 处理队列中的请求
    requestQueue.forEach(resolve => resolve(sessionInfo));
    requestQueue.length = 0;
    
    console.log(`⏱️ [Session] refreshSessionKey 总耗时: ${(performance.now() - startTime).toFixed(2)}ms`);
    return sessionInfo;
  } catch (error) {
    // 重试逻辑
    if (retryCount < MAX_RETRY_COUNT) {
      retryCount++;
      console.warn(`⏱️ [Session] Session 获取失败，第${retryCount}次重试，错误:`, error.message);
      
      // 延迟重试，避免立即失败
      await new Promise(resolve => setTimeout(resolve, 1000));
      return refreshSessionKey();
    } else {
      // 重置计数
      retryCount = 0;
      // 通知队列中的请求失败
      requestQueue.forEach(resolve => resolve(null));
      requestQueue.length = 0;
      
      console.error('⏱️ [Session] Session 获取失败，已达到最大重试次数', error);
      throw error;
    }
  } finally {
    isRefreshing = false;
  }
}

/**
 * 检查 session_key 是否有效
 * @param {number} advanceMinutes - 提前多少分钟判定为需要更新，默认2分钟
 * @returns {boolean} session_key 是否有效
 */
export function isSessionKeyValid(advanceMinutes = 2) {
  const session_key = util.localStorage.get('session_key')
  const expires_time = util.localStorage.get('session_key_expires_time')
  
  if (!session_key || !expires_time) {
    console.log('📋 session_key检查: 缓存中没有session_key或过期时间')
    return false
  }
  
  // 检查是否已过期（提前advanceMinutes分钟）
  const now = new Date().getTime()
  const expireTime = new Date(expires_time).getTime()
  const remainingTimeMs = expireTime - now
  const remainingMinutes = Math.floor(remainingTimeMs / (60 * 1000))
  const remainingHours = Math.floor(remainingMinutes / 60)
  const remainingMins = remainingMinutes % 60
  
  // 添加详细日志
  console.log(
    `📋 session_key状态: ${remainingTimeMs > 0 ? '有效' : '已过期'}, ` +
    `剩余时间: ${remainingHours}小时${remainingMins}分钟, ` +
    `提前量: ${advanceMinutes}分钟, ` +
    `是否需要刷新: ${remainingTimeMs < advanceMinutes * 60 * 1000 ? '是' : '否'}`
  )
  
  return now < (expireTime - advanceMinutes * 60 * 1000)
}

/**
 * 检查并确保 session_key 有效
 * @returns {Promise<Object>} session 信息对象
 */
export async function ensureSessionKey() {
  console.log('📋 ensureSessionKey: 开始检查session_key是否有效')
  
  // 检查是否有有效的 session_key
  if (isSessionKeyValid()) {
    const sessionInfo = JSON.parse(util.localStorage.get('session_info') || '{}')
    console.log('📋 ensureSessionKey: 使用缓存中的session_key', {
      user_id: sessionInfo.user_id,
      session_key: sessionInfo.session_key ? sessionInfo.session_key.substr(0, 10) + '...' : 'null',
      expires_time: sessionInfo.expires_time
    })
    return sessionInfo
  }
  
  // 获取新的 session_key
  console.log('📋 ensureSessionKey: 缓存中的session_key无效或即将过期，开始获取新的session_key')
  return refreshSessionKey()
}

/**
 * 处理 Session 错误 - 被动检测方式，保留但不推荐使用
 * @param {Error} error 错误对象
 * @returns {Promise<boolean>} 是否为 session 错误并已处理
 */
export function handleSessionError(error) {
  // 注意：此方法已被主动检测方式替代，仅作为备用机制保留
  // 检查是否为 session 相关错误
  const responseData = error.response?.data
  
  if (responseData && 
      (responseData.status_code === 401 || 
       responseData.message === 'session_key失效' ||
       responseData.message === 'session_key验证失败')) {
    
    console.warn('检测到session_key过期错误，尝试刷新');
    // 同时启动定时器，确保后续主动检测
    startSessionCheckTimer();
    
    return refreshSessionKey().then(() => true)
  }
  
  return Promise.resolve(false)
}

/**
 * 启动session_key定时检查
 * @param {number} interval - 检查间隔，单位毫秒，默认3分钟
 */
export function startSessionCheckTimer(interval = 3 * 60 * 1000) {
  // 清除可能存在的旧定时器
  stopSessionCheckTimer();
  
  console.log(`📋 启动session_key定时检查，间隔: ${interval/1000}秒`);
  
  // 创建新定时器
  sessionCheckTimer = setInterval(async () => {
    try {
      console.log('📋 定时检查: 开始检查session_key状态...');
      
      // 只有当有用户ID时才检查session_key
      const user_id = getUserId();
      if (!user_id) {
        console.log('📋 定时检查: 无用户ID，跳过session_key检查');
        return;
      }
      
      // 先打印当前session_key状态
      const session_key = util.localStorage.get('session_key')
      const expires_time = util.localStorage.get('session_key_expires_time')
      
      if (session_key && expires_time) {
        const now = new Date().getTime()
        const expireTime = new Date(expires_time).getTime()
        const remainingTimeMs = expireTime - now
        const remainingMinutes = Math.floor(remainingTimeMs / (60 * 1000))
        const remainingHours = Math.floor(remainingMinutes / 60)
        const remainingMins = remainingMinutes % 60
      
        console.log(
          `📋 定时检查: session_key状态: ${remainingTimeMs > 0 ? '有效' : '已过期'}, ` +
          `剩余时间: ${remainingHours}小时${remainingMins}分钟, ` +
          `session_key: ${session_key.substr(0, 10)}...`
        )
      }
      
      // 检查session_key是否有效，使用提前20分钟的判断标准
      if (!isSessionKeyValid(20)) {
        console.log('📋 定时检查: session_key已过期或即将过期，开始刷新');
        // 显示刷新提示（可选，透明度较低，不影响用户操作）
        const toast = Toast.loading({
          message: '更新会话信息...',
          forbidClick: false,
          duration: 0
        });
        
        // 刷新session_key
        const result = await refreshSessionKey();
        
        // 关闭提示
        toast.clear();
        console.log('📋 定时检查: session_key已成功更新, 新session_key:', 
          result?.session_key ? result.session_key.substr(0, 10) + '...' : '获取失败');
      } else {
        // 获取剩余有效时间
        const expires_time = util.localStorage.get('session_key_expires_time');
        if (expires_time) {
          const now = new Date().getTime();
          const expireTime = new Date(expires_time).getTime();
          const remainMinutes = Math.round((expireTime - now) / (60 * 1000));
          const remainHours = Math.floor(remainMinutes / 60);
          const remainMins = remainMinutes % 60;
          console.log(`📋 定时检查: session_key状态良好，剩余有效时间: ${remainHours}小时${remainMins}分钟`);
        }
      }
    } catch (error) {
      console.error('📋 定时检查session_key出错:', error);
      // 出错时不显示Toast，避免影响用户
    }
  }, interval);
  
  // 确保页面关闭时清除定时器
  window.addEventListener('beforeunload', stopSessionCheckTimer);
  
  return true;
}

/**
 * 停止session_key定时检查
 */
export function stopSessionCheckTimer() {
  if (sessionCheckTimer) {
    clearInterval(sessionCheckTimer);
    sessionCheckTimer = null;
    console.log('停止session_key定时检查');
  }
}

/**
 * 手动刷新session_key
 * @returns {Promise<boolean>} 是否刷新成功
 */
export async function manualRefreshSession() {
  try {
    Toast.loading({
      message: '更新会话凭证...',
      forbidClick: true,
      duration: 0
    });
    
    const result = await refreshSessionKey();
    
    Toast.success('会话已更新');
    return !!result;
  } catch (error) {
    console.error('手动刷新session_key失败:', error);
    Toast.fail('会话更新失败');
    return false;
  } finally {
    Toast.clear();
  }
}

/**
 * 清除 Session 信息
 */
export function clearSessionInfo() {
  util.localStorage.remove('session_key')
  util.localStorage.remove('session_key_expires_time')
  util.localStorage.remove('session_info')
  // 停止定时器
  stopSessionCheckTimer();
}

/**
 * 获取 session_key 和 user_id
 * @returns {Object} 包含 session_key 和 user_id 的对象
 */
export function getSessionInfo() {
  const session_key = util.localStorage.get('session_key')
  const user_id = getUserId()
  
  if (!session_key || !user_id) {
    return null
  }
  
  return { session_key, user_id }
}

// 导出
export default {
  refreshSessionKey,
  ensureSessionKey,
  handleSessionError,  // 保留但不推荐使用
  clearSessionInfo,
  getSessionInfo,
  isSessionKeyValid,
  // 新增
  startSessionCheckTimer,
  stopSessionCheckTimer,
  manualRefreshSession
}